import { homedir } from 'node:os';
import { join } from 'node:path';
import chalk from 'chalk';
import ora from 'ora';
import { api, ApiError, getHostId } from '../lib/api-client.js';
import { notProvisionedMessage } from '../lib/integration-provisioned-message.js';
import { probeScopeNote } from '../lib/probe-scope-note.js';
import { success, error, info } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';
import {
  executeConnectivityProbe,
  type ConnectivityProbeTarget,
} from '../lib/connectivity-probe-executor.js';
import {
  buildProbeEnv,
  buildConnectivityProbeDeps,
  deriveMcpServerKey,
} from '../lib/connectivity-probe-context.js';
import type { ToolkitSourceType, ConnectivityTestOverride } from '@augmented/core/integrations';
import type { IntegrationAuthType } from '@augmented/core';

interface IntegrationSummary {
  id: string;
  name: string;
  slug: string;
  category: string;
  status: string;
  version: number;
  skills: unknown[];
  defined_scopes: unknown[];
}

interface IntegrationDetail extends IntegrationSummary {
  description: string | null;
  required_toolkits: string[];
  allowed_tools: string[];
  scripts: Record<string, string>;
  defined_scopes: Array<{
    id: string;
    name: string;
    description: string;
    default_min_role: string;
  }>;
}

interface AgentIntegrationRow {
  id: string;
  agent_id: string;
  plugin_id: string;
  plugin_version: number;
  granted_scopes: string[];
  installed_at: string;
}

interface ScopeRequest {
  id: string;
  agent_id: string;
  plugin_id: string;
  requested_scopes: string[];
  reason: string | null;
  status: string;
  requested_by: string;
  created_at: string;
}

/**
 * ENG-7330: resolve THIS HOST's agents from `POST /host/agents`.
 * (Was ENG-6472's `GET /agents`, which is team-scoped — see the body.)
 *
 * The route returns `{ agents: [...] }` and the api client returns the raw body
 * UNWRAPPED — so callers must read `body.agents`, never treat the body as a bare
 * array. That contract is unchanged by the route swap. Treating it as an array
 * was the `agents.find is not a function` TypeError that crashed the host-side
 * `agt integration probe` (ENG-6441) over SSM — which in turn left the xero MCP
 * presence check perpetually failing and drove mcp-presence-reaper flapping on
 * sterling. install/uninstall/enrol shared the identical latent bug. Centralised
 * here so the unwrap + non-array guard lives in exactly one place: a missing or
 * non-array `agents` degrades to `[]` (caller reports "agent not found") instead
 * of throwing.
 */
async function fetchAgents(): Promise<Array<{ agent_id: string; code_name: string }>> {
  // ENG-7330: resolve the roster HOST-scoped, not team-scoped.
  //
  // This used to call `GET /agents`, which lives in the team-scoped route group
  // and needs an `X-Team-Slug` header. The CLI derives that header from
  // `getActiveTeam() ?? exchange.teamSlug` (api-client.ts), so on a host with no
  // team binding it resolves to nothing, the header is omitted, the route
  // returns an empty roster, and the caller reports `Agent "<name>" not found`.
  //
  // That message reads as a broken integration, which is exactly how ENG-8352
  // came to be diagnosed as one: `debug_probe_integration` returned
  // `probe_error` for healthy, actively heart-beating agents, and an
  // `integration_down` alert was opened naming an integration nobody had
  // measured. The AGT_TEAM preflight added downstream was treating this
  // symptom - it demanded a variable purely so this one lookup would work.
  //
  // Hosts are no longer required to be bound to a team, so the team was never
  // the right key here. `POST /host/agents` is host-authenticated (ENG-5399:
  // bound to the host the JWT was minted for) and the host id comes from the
  // API-key exchange itself, so this path now needs no team at any layer. Same
  // call the manager-worker and `agt host agents` already make.
  // Two-branch, mirroring the split `agt host agents` already uses: prefer the
  // host-scoped roster when we are authenticated as a host (every host-side
  // invocation, including the SSM probe), and keep the team-scoped route for an
  // operator running install/uninstall/enrol from their own machine with no host
  // identity. Host-side callers stop depending on a team; nobody else changes.
  const hostId = await getHostId().catch(() => null);
  const body = hostId
    ? await api.post<{ agents?: Array<{ agent_id: string; code_name: string }> }>('/host/agents', {
        host_id: hostId,
      })
    : await api.get<{ agents?: Array<{ agent_id: string; code_name: string }> }>('/agents');
  return Array.isArray(body?.agents) ? body.agents : [];
}

// ── List ──────────────────────────────────────────────────────────────────

export async function integrationListCommand(): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Fetching integrations…', isSilent: json }).start();

  try {
    const data = await api.get<IntegrationSummary[]>('/integrations/installs');
    spinner.stop();

    if (json) {
      jsonOutput({ integrations: data });
      return;
    }

    if (!data.length) {
      info('No integrations found for this team.');
      return;
    }

    console.log(chalk.bold('\nAvailable Integrations:\n'));
    for (const p of data) {
      const statusColor = p.status === 'published' ? chalk.green : p.status === 'archived' ? chalk.gray : chalk.yellow;
      console.log(
        `  ${chalk.bold(p.name)} ${chalk.dim(`(${p.slug})`)}  ${statusColor(p.status)} v${p.version}  ${chalk.dim(`${p.skills?.length ?? 0} skills, ${p.defined_scopes?.length ?? 0} scopes`)}`,
      );
    }
    console.log();
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Show ──────────────────────────────────────────────────────────────────

export async function integrationShowCommand(slug: string): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Fetching integration…', isSilent: json }).start();

  try {
    // List all and find by slug
    const all = await api.get<IntegrationDetail[]>('/integrations/installs');
    const integration = all.find((p) => p.slug === slug);

    if (!integration) {
      spinner.stop();
      error(`Integration "${slug}" not found.`);
      process.exitCode = 1;
      return;
    }

    // Fetch effective scopes
    const scopes = await api.get<Array<{
      id: string;
      name: string;
      effective_min_role: string;
      is_overridden: boolean;
      can_grant: boolean;
    }>>(`/integrations/installs/${integration.id}/scopes`);

    spinner.stop();

    if (json) {
      jsonOutput({ integration, scopes });
      return;
    }

    console.log(chalk.bold(`\n${integration.name}`), chalk.dim(`(${integration.slug})`));
    if (integration.description) console.log(chalk.dim(integration.description));
    console.log();
    console.log(`  Status:    ${integration.status} v${integration.version}`);
    console.log(`  Category:  ${integration.category}`);
    console.log(`  Toolkits:  ${integration.required_toolkits.join(', ') || 'none'}`);
    console.log(`  Skills:    ${integration.skills?.length ?? 0}`);

    if (scopes.length > 0) {
      console.log(chalk.bold('\n  Permission Scopes:\n'));
      for (const s of scopes) {
        const roleColor = s.can_grant ? chalk.green : chalk.red;
        const overrideTag = s.is_overridden ? chalk.cyan(' [team override]') : '';
        console.log(
          `    ${chalk.bold(s.id)}  ${s.name}  min: ${roleColor(s.effective_min_role)}${overrideTag}  ${s.can_grant ? chalk.green('✓ grantable') : chalk.red('✗ needs approval')}`,
        );
      }
    }
    console.log();
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Probe (ENG-6441) ────────────────────────────────────────────────────────

/** The subset of `/host/agent-integrations` rows the probe needs. */
interface HostAgentIntegrationRow {
  id: string;
  definition_id: string;
  credentials: Record<string, unknown>;
  auth_type: string;
  source_type?: string | null;
  connectivity_test?: ConnectivityTestOverride | null;
  // ENG-7543: which connection of the toolkit this row is (default | slug).
  connection_key?: string | null;
  // ENG-9588: the row's age. `/host/agent-integrations` has returned this since
  // ENG-7575 and this lane never read it, which is the whole defect: with
  // `createdAt` undefined, `isWithinFirstConnectWindow` fails open (its
  // deliberate "escalate only on positive evidence of age" rule), the 15-minute
  // window is permanently open, and `verdictForUnavailableMcpConfig`'s
  // `key-absent` branch can never reach its final line — the `down` /
  // "not wired in .mcp.json" verdict its own comment calls "the one genuine
  // assertion in this function". Measured: `sherlock`'s github at 37 consecutive
  // failures and `penny`'s lovable at 122 both forced-probed `unverified`
  // "within first-connect window", against a fifteen-minute window.
  created_at?: string | null;
  /**
   * ENG-9718 — the reconnect settling deadline, forwarded by
   * `/host/agent-integrations` alongside `created_at`. A row reconnected
   * moments ago has a `.mcp.json` the host has not rewritten yet, so its
   * missing key must read `unverified`, not `down`.
   */
  reconnect_quiesced_until?: string | null;
  // ENG-5463: the toolkit's CLI binary. Without it a forced probe of github /
  // gcloud / xurl / greenlight / expo resolves to `builtin` and prints
  // "no live check available" having run nothing — which is precisely what
  // `agt integration probe marlow github` did, and the reason this ticket
  // exists. The async manager lane already carried this field
  // (connectivity-probe-runner.ts); the forced-probe lane did not.
  cli_binary?: string | null;
}

/**
 * ENG-9588 — build the forced lane's probe target, EXPORTED so it can be driven
 * by a test.
 *
 * This was an object literal inside {@link integrationProbeCommand}, which is a
 * network-bound command no unit test can reach. That is not incidental to the
 * bug it now carries a guard for: the forced lane has silently dropped a field
 * the scheduled lane carries three times (ENG-5463 `cli_binary`, ENG-7077
 * `remoteMcpUrl`, ENG-9588 `createdAt`), and each time the omission was
 * unreachable from a test, so each was found in production by someone reading a
 * verdict that made no sense.
 *
 * `expectedAbsent` is hardcoded `false` rather than threaded, and that is a
 * claim worth stating: `/host/agent-integrations` does not serve quarantined
 * rows at all — they come from `/host/agent-integrations/quarantined`, which
 * this lane never queries. So every row reaching here is by construction
 * un-quarantined. The visible consequence is that a forced probe of a
 * quarantined integration reports `not_installed` rather than
 * "absence expected"; that is a separate gap (it is how `horatio`'s Salesforce
 * presents today) and is deliberately not fixed here.
 */
export function buildForcedProbeTarget(
  row: HostAgentIntegrationRow,
  ctx: {
    sourceType: ToolkitSourceType | null;
    authType: IntegrationAuthType;
    connectivityTest: ConnectivityTestOverride | null;
    remoteMcpUrl: string | null;
  },
): ConnectivityProbeTarget {
  return {
    definitionId: row.definition_id,
    sourceType: ctx.sourceType,
    authType: ctx.authType,
    credentials: row.credentials ?? {},
    mcpServerKey: deriveMcpServerKey({
      definitionId: row.definition_id,
      sourceType: ctx.sourceType,
      authType: ctx.authType,
      connectivityTest: ctx.connectivityTest,
      // ENG-7543: probe this connection's own server (find() picks the first
      // connection for the slug; default fleet is single-connection = 'default').
      connectionKey: row.connection_key ?? null,
      remoteMcpUrl: ctx.remoteMcpUrl,
    }),
    connectivityTest: ctx.connectivityTest,
    remoteMcpUrl: ctx.remoteMcpUrl,
    // ENG-5463: see the note on `cli_binary` in HostAgentIntegrationRow.
    cliBinary: row.cli_binary ?? undefined,
    // ENG-9588: the two fields the scheduled lane has always set and this one
    // never did. See the note on `created_at` in HostAgentIntegrationRow.
    createdAt: row.created_at ?? null,
    // ENG-9718: the third such field, and the reason the type requires it. This
    // lane is `agt integration probe` — a human asking "is it working right
    // now?" — so a reconnect seconds earlier is exactly when it gets run.
    reconnectQuiescedUntil: row.reconnect_quiesced_until ?? null,
    expectedAbsent: false,
  };
}

/**
 * ENG-6441: force a FRESH host-side connectivity probe for ONE installed
 * integration and print the live verdict — NOT the cached `last_connectivity_*`
 * the central `POST /integrations/:id/test` echoes. This is the host primitive
 * the admin-debug `debug_probe_integration` tool invokes over SSM (PR B).
 *
 * Runs on the host: resolves the agent's own `.mcp.json` + `.env.integrations`
 * via the SHARED probe context (buildConnectivityProbeDeps) — the same one the
 * manager's hourly loop uses, so the on-demand verdict can't disagree with it —
 * then calls `executeConnectivityProbe` directly (bypassing the loop's due-gate,
 * which is the whole point: a fresh check on demand).
 */
export async function integrationProbeCommand(
  agentCodeName: string,
  slug: string,
): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: `Probing ${slug} on ${agentCodeName}…`, isSilent: json }).start();

  try {
    // 1. Resolve the agent's UUID from its code_name.
    const agents = await fetchAgents();
    const agent = agents.find((a) => a.code_name === agentCodeName);
    if (!agent) {
      spinner.stop();
      error(`Agent "${agentCodeName}" not found.`);
      process.exitCode = 1;
      return;
    }

    // 2. Fetch the agent's host integrations — decrypted credentials + the probe
    //    inputs (source_type, auth_type, connectivity_test). Host-authenticated.
    const data = await api.post<{ integrations: HostAgentIntegrationRow[] }>(
      '/host/agent-integrations',
      { agent_id: agent.agent_id },
    );
    const integrations = data.integrations ?? [];
    // ENG-10488: `find()` takes the FIRST row under the slug, and since ENG-7543
    // a toolkit can hold several connections. Capture the whole match set so the
    // verdict can name its own scope instead of implying a wider one.
    const matches = integrations.filter(
      (i) => i.definition_id.toLowerCase() === slug.toLowerCase(),
    );
    const integ = matches[0];
    if (!integ) {
      spinner.stop();
      // ENG-10196: this list is the PROVISIONED set, not everything installed —
      // the endpoint filters status to active/configured, while `error` and
      // `unhealthy` rows stay probeable (PROBE_RESCUE_STATUSES). Asserting
      // absence from it was the defect; see notProvisionedMessage.
      error(
        notProvisionedMessage({
          slug,
          agentCodeName,
          provisioned: integrations.map((i) => i.definition_id),
        }),
      );
      process.exitCode = 1;
      return;
    }

    // 3. Build the shared host probe context + the single-integration target.
    const projectDir = join(homedir(), '.augmented', agentCodeName, 'project');
    // ENG-9122 AC3: same env as the scheduled lane — a forced probe must run
    // the identical context, or admin-debug measures something the monitor does not.
    const probeEnv = buildProbeEnv(projectDir, agent.agent_id);
    const probeDeps = buildConnectivityProbeDeps(projectDir, probeEnv);
    const sourceType = (integ.source_type ?? null) as ToolkitSourceType | null;
    const authType = integ.auth_type as IntegrationAuthType;
    const connectivityTest = (integ.connectivity_test ?? null) as ConnectivityTestOverride | null;
    // ENG-7077: the catalog remote-MCP url, so a forced probe of monday/peec/
    // anchor-browser resolves to a real `tools/list` handshake rather than the
    // `builtin` no-op. This is the path admin-debug's forced probe runs through,
    // so without it a "forced probe" reports `unverified` having done nothing.
    const remoteMcpUrl =
      (integ as { remote_mcp?: { url?: string | null } | null }).remote_mcp?.url ?? null;
    const target = buildForcedProbeTarget(integ, { sourceType, authType, connectivityTest, remoteMcpUrl });

    // 4. Force a fresh probe (no due-gate, unlike the manager's rolling loop).
    const outcome = await executeConnectivityProbe(target, probeDeps);
    spinner.stop();

    // `new Date()` is fine in a one-shot CLI command (not a workflow/resume path).
    const probedAt = new Date().toISOString();

    // ENG-10488: the scope this verdict actually covers, computed once so the
    // JSON and human shapes cannot drift.
    const siblingKeys = matches
      .slice(1)
      .map((m) => m.connection_key ?? '(no connection_key)');
    const scopeNote = probeScopeNote({
      slug: integ.definition_id,
      agentCodeName,
      connectionKey: integ.connection_key ?? null,
      visibleSiblingKeys: siblingKeys,
    });

    if (outcome === null) {
      // No probe is wired for this integration kind (e.g. a built-in with no
      // remote to reach). Report it plainly rather than inventing a status.
      const result = {
        agent: agentCodeName,
        slug: integ.definition_id,
        connection_key: integ.connection_key ?? null,
        sibling_connection_keys: siblingKeys,
        scope_note: scopeNote,
        status: 'not_probeable',
        message: 'no connectivity probe is available for this integration kind',
        probed_at: probedAt,
      };
      if (json) {
        jsonOutput(result);
        return;
      }
      info(`${integ.definition_id}: not probeable (no probe wired for this integration kind)`);
      console.log(chalk.dim(scopeNote.split('\n').map((l) => (l ? `  ${l}` : '')).join('\n')));
      return;
    }

    const result = {
      agent: agentCodeName,
      slug: integ.definition_id,
      // ENG-10488: WHICH connection this verdict is about. Absent, a consumer
      // (the admin-debug tool that shells this over SSM included) cannot tell a
      // single-connection integration from the first of several.
      connection_key: integ.connection_key ?? null,
      sibling_connection_keys: siblingKeys,
      scope_note: scopeNote,
      status: outcome.status,
      message: outcome.message ?? null,
      probed_at: probedAt,
    };
    if (json) {
      jsonOutput(result);
      return;
    }

    const color =
      outcome.status === 'ok'
        ? chalk.green
        : outcome.status === 'down'
          ? chalk.red
          : chalk.yellow; // transient_error | degraded
    console.log(
      `${chalk.bold(integ.definition_id)} on ${chalk.bold(agentCodeName)}: ${color(outcome.status)}`,
    );
    if (outcome.message) console.log(chalk.dim(`  ${outcome.message}`));
    // ENG-10488: printed for EVERY verdict, including `ok`. An absent-branch bug
    // gets investigated; a present-branch bug gets believed, so the scope has to
    // travel with the answer people stop reading at.
    console.log(chalk.dim(scopeNote.split('\n').map((l) => (l ? `  ${l}` : '')).join('\n')));
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Install ───────────────────────────────────────────────────────────────

export async function integrationInstallCommand(
  slug: string,
  options: { agent: string; scopes?: string },
): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Installing integration…', isSilent: json }).start();

  try {
    // Resolve integration ID from slug
    const all = await api.get<IntegrationDetail[]>('/integrations/installs');
    const integration = all.find((p) => p.slug === slug);
    if (!integration) {
      spinner.stop();
      error(`Integration "${slug}" not found.`);
      process.exitCode = 1;
      return;
    }

    // Resolve agent ID from code name
    const agents = await fetchAgents();
    const agent = agents.find((a) => a.code_name === options.agent);
    if (!agent) {
      spinner.stop();
      error(`Agent "${options.agent}" not found.`);
      process.exitCode = 1;
      return;
    }

    const scopes = options.scopes?.split(',').map((s) => s.trim()).filter(Boolean) ?? [];

    const result = await api.post<AgentIntegrationRow>('/integrations/installs/install', {
      agent_id: agent.agent_id,
      plugin_id: integration.id,
      scopes,
    });

    spinner.stop();

    if (json) {
      jsonOutput({ agent_integration: result });
      return;
    }

    success(`Installed "${integration.name}" on agent "${options.agent}"`);
    if (scopes.length) {
      info(`Granted scopes: ${scopes.join(', ')}`);
    }
  } catch (err) {
    spinner.stop();
    if (err instanceof ApiError && err.status === 403 && err.body['needs_approval']) {
      const needsApproval = err.body['needs_approval'] as string[];
      error('Some scopes exceed your role ceiling:');
      for (const s of needsApproval) {
        console.log(chalk.yellow(`  - ${s}`));
      }
      info('To request elevated scopes, use the web dashboard or contact your team admin.');
      process.exitCode = 1;
      return;
    }
    handleError(err);
  }
}

// ── Uninstall ─────────────────────────────────────────────────────────────

export async function integrationUninstallCommand(
  slug: string,
  options: { agent: string },
): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Uninstalling integration…', isSilent: json }).start();

  try {
    const all = await api.get<IntegrationDetail[]>('/integrations/installs');
    const integration = all.find((p) => p.slug === slug);
    if (!integration) { spinner.stop(); error(`Integration "${slug}" not found.`); process.exitCode = 1; return; }

    const agents = await fetchAgents();
    const agent = agents.find((a) => a.code_name === options.agent);
    if (!agent) { spinner.stop(); error(`Agent "${options.agent}" not found.`); process.exitCode = 1; return; }

    await api.post('/integrations/installs/uninstall', {
      agent_id: agent.agent_id,
      plugin_id: integration.id,
    });
    spinner.stop();
    success(`Uninstalled "${integration.name}" from agent "${options.agent}"`);
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Scope Requests ────────────────────────────────────────────────────────

export async function integrationRequestsCommand(): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Fetching scope requests…', isSilent: json }).start();

  try {
    const requests = await api.get<ScopeRequest[]>('/integrations/installs/scope-requests');
    spinner.stop();

    if (json) {
      jsonOutput({ requests });
      return;
    }

    if (!requests.length) {
      info('No pending scope requests.');
      return;
    }

    console.log(chalk.bold('\nPending Scope Requests:\n'));
    for (const r of requests) {
      console.log(`  ${chalk.bold(r.id)}  scopes: ${r.requested_scopes.join(', ')}`);
      if (r.reason) console.log(`    reason: ${chalk.dim(r.reason)}`);
      console.log(`    status: ${r.status}  requested: ${r.created_at}`);
      console.log();
    }
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Approve ───────────────────────────────────────────────────────────────

export async function integrationApproveCommand(
  requestId: string,
  options: { notes?: string },
): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Approving request…', isSilent: json }).start();

  try {
    const result = await api.put(`/integrations/installs/scope-requests/${requestId}`, {
      status: 'approved',
      review_notes: options.notes,
    });
    spinner.stop();

    if (json) {
      jsonOutput(result);
      return;
    }

    success(`Scope request ${requestId} approved. Scopes have been granted.`);
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Deny ──────────────────────────────────────────────────────────────────

export async function integrationDenyCommand(
  requestId: string,
  options: { reason?: string },
): Promise<void> {
  const json = isJsonMode();
  const spinner = ora({ text: 'Denying request…', isSilent: json }).start();

  try {
    const result = await api.put(`/integrations/installs/scope-requests/${requestId}`, {
      status: 'denied',
      review_notes: options.reason,
    });
    spinner.stop();

    if (json) {
      jsonOutput(result);
      return;
    }

    success(`Scope request ${requestId} denied.`);
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Enrol ─────────────────────────────────────────────────────────────────
//
// ENG-4962: wraps POST /integrations/ for API-key onboarding so adding a
// new integration is one command instead of raw SQL. The endpoint runs
// the ENG-4953 fail-closed encryption gate, so a credential supplied here
// is encrypted before it lands in organization_integrations /
// team_integrations / agent_integrations.
//
// OAuth integrations follow a different path (callback-driven); this
// command intentionally only covers api_key / webhook auth types where
// the secret is known at enrolment time.

interface EnrolledIntegrationRow {
  id: string;
  definition_id: string;
  status: string;
}

interface EnrolOptions {
  def: string;
  scope: 'org' | 'team' | 'agent' | 'organization';
  apiKey?: string;
  accessToken?: string;
  webhookSecret?: string;
  displayName?: string;
  agent?: string;
  authType?: 'api_key' | 'oauth2' | 'webhook';
}

export async function integrationEnrolCommand(options: EnrolOptions): Promise<void> {
  const json = isJsonMode();

  // Normalise scope: `org` is the runtime-scope vocabulary; the API
  // accepts the historical `organization` form. Both map to the same row.
  const apiScope = options.scope === 'org' ? 'organization' : options.scope;
  if (!['organization', 'team', 'agent'].includes(apiScope)) {
    error(`Invalid scope "${options.scope}". Use org, team, or agent.`);
    process.exitCode = 1;
    return;
  }

  // Build the credentials object from the secret-bearing flags. The
  // endpoint accepts {access_token} (OAuth-style) and {api_key} as
  // alternative bearer fields — exactly one secret flag should be set.
  const secrets: Array<[string, string]> = [];
  if (options.apiKey) secrets.push(['api_key', options.apiKey]);
  if (options.accessToken) secrets.push(['access_token', options.accessToken]);
  if (options.webhookSecret) secrets.push(['webhook_secret', options.webhookSecret]);
  if (secrets.length === 0) {
    error('One of --api-key, --access-token, or --webhook-secret is required.');
    process.exitCode = 1;
    return;
  }
  if (secrets.length > 1) {
    error('Only one of --api-key, --access-token, --webhook-secret may be set.');
    process.exitCode = 1;
    return;
  }
  const [secretKey, secretValue] = secrets[0]!;
  const credentials: Record<string, string> = { [secretKey]: secretValue };

  const authType = options.authType
    ?? (options.webhookSecret ? 'webhook'
      : options.apiKey ? 'api_key'
      : 'oauth2');

  const spinner = ora({ text: `Enrolling ${options.def} at ${apiScope} scope…`, isSilent: json }).start();

  try {
    // For agent scope, resolve the agent's UUID from its code_name. Team
    // scope auto-resolves from the team header; org scope derives from
    // the team's organization_id server-side (ENG-4962 endpoint tweak).
    let agentId: string | undefined;
    if (apiScope === 'agent') {
      if (!options.agent) {
        spinner.stop();
        error('--agent <code-name> is required for agent scope.');
        process.exitCode = 1;
        return;
      }
      const agents = await fetchAgents();
      const found = agents.find((a) => a.code_name === options.agent);
      if (!found) {
        spinner.stop();
        error(`Agent "${options.agent}" not found.`);
        process.exitCode = 1;
        return;
      }
      agentId = found.agent_id;
    }

    // Resolve a display_name from the definition if the caller didn't
    // supply one. Saves the user from having to type "Resend" twice.
    let displayName = options.displayName;
    if (!displayName) {
      const defs = await api.get<IntegrationDetail[]>('/integrations/installs');
      const def = defs.find((d) => d.slug === options.def);
      displayName = def?.name ?? options.def;
    }

    const result = await api.post<{ integration: EnrolledIntegrationRow }>('/integrations', {
      scope: apiScope,
      definition_id: options.def,
      display_name: displayName,
      auth_type: authType,
      credentials,
      config: {},
      ...(agentId ? { agent_id: agentId } : {}),
    });

    spinner.stop();

    const row = result.integration;
    if (json) {
      jsonOutput({ integration: row });
      return;
    }

    success(`Enrolled "${displayName}" (${options.def}) at ${apiScope} scope`);
    info(`Integration ID: ${row.id}`);
    info(`Status:         ${row.status}`);
  } catch (err) {
    spinner.stop();
    handleError(err);
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────

function handleError(err: unknown): void {
  if (err instanceof ApiError) {
    error(`API error (${err.status}): ${err.message}`);
  } else {
    error((err as Error).message);
  }
  process.exitCode = 1;
}
